import "../global.css";
import Layout from "../components/Layout";
import Beranda from "../pages/Beranda";
import Blog from "../pages/Blog";
import BlogDetail from "../pages/BlogDetail";
import useCustomToast from "../lib/useCustomToast";

import { ChakraProvider } from "@chakra-ui/react";
import PullToRefresh from "pulltorefreshjs";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import { BaseAuth } from "../components/BaseAuth";
import TawkToChat from "../components/button/TawkToChat";
import { LimitWidthContainer } from "../components/container/LimitWidthContainer";
import { LayoutDashboardBuyer } from "../components/layout/LayoutDashboardBuyer";
import { LayoutDashboardReseller } from "../components/layout/LayoutDashboardReseller";
import { LayoutDashboardSupplier } from "../components/layout/LayoutDashboardSupplier";
import { SpinnerLoading } from "../components/spinner/SpinnerLoading";
import { CurrentActiveUserInterface } from "../constants/User";
import { BuyerOnlyRoute } from "../middleware/BuyerOnlyRoute";
import { EmailConfirmation } from "../pages/auth/EmailConfimation";
import { Login } from "../pages/auth/Login";
import { Register } from "../pages/auth/Register";
import { ResetPassword } from "../pages/auth/ResetPassword";
import { BuyerChat } from "../pages/dashboard/buyer/BuyerChat";
import { DetailOrder } from "../pages/dashboard/buyer/DetailOrder";
import { Favorite } from "../pages/dashboard/buyer/Favorite";
import { OrderStatus } from "../pages/dashboard/buyer/OrderStatus";
import { DashboardReseller } from "../pages/dashboard/reseller/DashboardReseller";
import { DetailProductSupplier } from "../pages/dashboard/reseller/DetailProductSupplier";
import { DetailSupplier } from "../pages/dashboard/reseller/DetailSupplier";
import { ListSupplier } from "../pages/dashboard/reseller/ListSupplier";
import { MyStoreDetailProduct } from "../pages/dashboard/reseller/MyStoreDetailProduct";
import { MyStoreProfile } from "../pages/dashboard/reseller/MyStoreProfile";
import { Withdraw } from "../pages/dashboard/reseller/Withdraw";
import { EditProductPage } from "../pages/dashboard/supplier/EditProductPage";
import { MyStoreSupplier } from "../pages/dashboard/supplier/MyStoreSupplier";
import { MyStoreSupplierProfile } from "../pages/dashboard/supplier/MyStoreSupplierProfile";
import { SupplierChat } from "../pages/dashboard/supplier/SupplierChat";
import GuidePage from "../pages/Guide";
import ProductPage from "../pages/Product";
import ProductDetail from "../pages/ProductDetail";
import ResellerProfile from "../pages/ResellerProfile";
import {
  onMessageListener,
  requestFCMToken,
} from "../services/firebase.config";
import { useGetUserLoginStore } from "../store/useGetUserLoginStore";
import { useTriggerRenderStore } from "../store/useTriggerRenderStore";
import { useUserHeader } from "../store/useUserHeader";
import customTheme from "../themes";
import {
  createVisitorId,
  saveFcmTokenToBackend,
} from "../utils/helperFunction";
import { ThemeToggler } from "../utils/themeToggler";
import AboutPage from "../pages/About";
import TermsAndConditions from "../pages/TermsAndConditions";

const Contact = lazy(() => import("../pages/Contact"));

const NotFound = lazy(() => import("../pages/NotFound"));

const AddProductPage = lazy(
  () => import("../pages/dashboard/supplier/AddProductPage")
);
const MyStoreSupplierDetailProduct = lazy(
  () => import("../pages/dashboard/supplier/MyStoreSupplierDetailProduct")
);
const DashboardSupplier = lazy(
  () => import("../pages/dashboard/supplier/DashboardSupplier")
);
const AccountSettings = lazy(
  () => import("../pages/dashboard/buyer/AccountSettings")
);
const MyStore = lazy(() => import("../pages/dashboard/reseller/MyStore"));
const ListProduct = lazy(
  () => import("../pages/dashboard/reseller/ListProduct")
);
const Cart = lazy(() => import("../pages/Cart"));

const Faq = lazy(() => import("../pages/Faq"));

const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));

const Transaction = lazy(() => import("../pages/Transaction"));

const ScrollToTop = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location]);
  return <>{children}</>;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ScrollToTop>
        <Layout />
      </ScrollToTop>
    ),
    children: [
      {
        index: true,
        element: <Beranda />,
      },
      {
        path: "about",
        element: <AboutPage />,
      },
      {
        path: "product",
        element: <ProductPage />,
      },
      {
        path: "product/:prefix/:slug",
        element: <ProductDetail />,
      },
      {
        path: "reseller/:prefix",
        element: <ResellerProfile />,
      },
      {
        path: "cart",
        element: (
          <BuyerOnlyRoute>
            <Suspense fallback={<SpinnerLoading />}>
              <Cart />
            </Suspense>
          </BuyerOnlyRoute>
        ),
      },
      {
        path: "blog",
        element: <Blog />,
      },
      {
        path: "blog/:slug",
        element: <BlogDetail />,
      },
      {
        path: "guide",
        element: <GuidePage />,
      },
      {
        path: "guide/:role",
        element: <GuidePage />,
      },
      {
        path: "faq",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <Faq />
          </Suspense>
        ),
      },
      {
        path: "terms-and-conditions",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <TermsAndConditions />
          </Suspense>
        ),
      },
      {
        path: "privacy-policy",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      {
        path: "contact",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <Contact />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "*",
    element: (
      <Suspense>
        <NotFound />
      </Suspense>
    ),
  },
  {
    path: "404",
    element: (
      <Suspense>
        <NotFound />
      </Suspense>
    ),
  },
  {
    path: "500-error",
    element: (
      <Suspense>
        <NotFound
          title="500 Server Error"
          subtitle="Maaf! Sistem kami sedang mengalami downtime namun jangan takut, kami sedang memperbaikinya"
          src="/assets/svg/illustration_500.svg"
        />
      </Suspense>
    ),
  },
  {
    path: "transaction",
    element: (
      <BuyerOnlyRoute>
        <LimitWidthContainer>
          <ScrollToTop>
            <Suspense fallback={<SpinnerLoading />}>
              <Transaction />
            </Suspense>
          </ScrollToTop>
        </LimitWidthContainer>
      </BuyerOnlyRoute>
    ),
  },
  {
    path: "email-confirmation",
    element: (
      <LimitWidthContainer>
        <EmailConfirmation />
      </LimitWidthContainer>
    ),
  },
  {
    path: "login",
    element: (
      <LimitWidthContainer mx={"auto"}>
        <BaseAuth breakpoint={640} />
      </LimitWidthContainer>
    ),
    children: [{ index: true, element: <Login /> }],
  },
  {
    path: "register",
    element: (
      <LimitWidthContainer mx={"auto"}>
        <BaseAuth breakpoint={768} />
      </LimitWidthContainer>
    ),
    children: [{ index: true, element: <Register /> }],
  },
  {
    path: "reset-password",
    element: (
      <LimitWidthContainer h={"100dvh"} maxW={"640px"} mx={"auto"}>
        <ResetPassword />
      </LimitWidthContainer>
    ),
  },
  // ROUTE DASHBOARD BUYER
  {
    path: "buyer",
    element: (
      <ScrollToTop>
        <LayoutDashboardBuyer />
      </ScrollToTop>
    ),
    children: [
      { index: true, element: <OrderStatus pageType={1} /> },
      {
        path: "order/:id",
        element: <DetailOrder pageType={1} />,
      },
      {
        path: "favorite",
        element: (
          <LimitWidthContainer>
            <Favorite />
          </LimitWidthContainer>
        ),
      },
      { path: "chat", element: <BuyerChat /> },
      {
        path: "account",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <AccountSettings pageType={1} />
          </Suspense>
        ),
      },
    ],
  },
  // ROUTE DASHBOARD RESELLER
  {
    path: "reseller",
    element: (
      <ScrollToTop>
        <LayoutDashboardReseller />
      </ScrollToTop>
    ),
    children: [
      { index: true, element: <DashboardReseller /> },
      { path: "withdraw", element: <Withdraw pageType={2} /> },
      { path: "order", element: <OrderStatus pageType={2} /> },
      {
        path: "order/:id",
        element: <DetailOrder pageType={2} />,
      },
      {
        path: "search/supplier",
        element: (
          <LimitWidthContainer>
            <ListSupplier />
          </LimitWidthContainer>
        ),
      },
      {
        path: "search/product",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <ListProduct />
          </Suspense>
        ),
      },
      {
        path: "search/supplier/:id",
        element: <DetailSupplier />,
      },
      {
        path: "search/product/:id",
        element: (
          <LimitWidthContainer>
            <DetailProductSupplier />
          </LimitWidthContainer>
        ),
      },
      {
        path: "mystore",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <LimitWidthContainer>
              <MyStore />
            </LimitWidthContainer>
          </Suspense>
        ),
      },
      {
        path: "mystore/product/:id",
        element: <MyStoreDetailProduct />,
      },
      {
        path: "mystore/profile",
        element: (
          <LimitWidthContainer>
            <MyStoreProfile />
          </LimitWidthContainer>
        ),
      },
      {
        path: "account",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <AccountSettings pageType={2} />
          </Suspense>
        ),
      },
    ],
  },
  // ROUTE DASHBOARD SUPPLIER
  {
    path: "supplier",
    element: (
      <ScrollToTop>
        <LayoutDashboardSupplier />
      </ScrollToTop>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <DashboardSupplier />
          </Suspense>
        ),
      },
      { path: "withdraw", element: <Withdraw pageType={3} /> },
      {
        path: "order",
        element: <OrderStatus pageType={3} />,
      },
      {
        path: "order/:id",
        element: <DetailOrder pageType={3} />,
      },
      {
        path: "mystore",
        element: (
          <LimitWidthContainer>
            <MyStoreSupplier />
          </LimitWidthContainer>
        ),
      },
      {
        path: "mystore/product/add",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <AddProductPage />
          </Suspense>
        ),
      },
      {
        path: "mystore/product/edit/:id",
        element: <EditProductPage />,
      },
      {
        path: "mystore/product/:id",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <LimitWidthContainer>
              <MyStoreSupplierDetailProduct />
            </LimitWidthContainer>
          </Suspense>
        ),
      },
      {
        path: "mystore/profile",
        element: <MyStoreSupplierProfile pageType={3} />,
      },
      { path: "chat", element: <SupplierChat /> },
      {
        path: "account",
        element: (
          <Suspense fallback={<SpinnerLoading />}>
            <AccountSettings pageType={3} />
          </Suspense>
        ),
      },
    ],
  },
]);

export const App = () => {
  const [alreadyRequest, setAlreadyRequest] = useState<boolean>(false);
  const { fetchNotificationCount } = useUserHeader();
  const { statusData, setStatusData } = useTriggerRenderStore();
  const toast = useCustomToast();

  const {
    currentActiveUser,
    loggedUsers,
    getCurrentActiveUser,
    saveCurrentActiveUser,
    saveLoggedUsers,
  } = useGetUserLoginStore();

  useEffect(() => {
    const visitorId = localStorage.getItem("visitorId");
    if (!visitorId) {
      console.log("createlagii");

      createVisitorId();
    }
  }, []);

  useEffect(() => {
    getCurrentActiveUser();
  }, [getCurrentActiveUser]);

  useEffect(() => {
    if (!currentActiveUser?.id) return;

    const setupFCM = async () => {
      const vapidKey = `${process.env.REACT_APP_VAPID_KEY}`;

      const fcmToken = await requestFCMToken(vapidKey);

      if (fcmToken) {
        setAlreadyRequest(true);
        // save token to backend
        // kalo token dari hasil generate sama dari token cookie maka tidak simpan di backend
        if (currentActiveUser.fcm_token === fcmToken) return;

        const response = await saveFcmTokenToBackend(
          currentActiveUser?.id,
          `${currentActiveUser?.role}`,
          fcmToken
        );

        saveCurrentActiveUser(response);
      } else {
        console.warn(
          "User didn't allow notifications or no fcm token available"
        );
      }
    };

    if ("Notification" in window) {
      if (Notification.permission === "granted") {
        if (!alreadyRequest) {
          setupFCM();
        }
      }
    }

    onMessageListener()
      .then((payload) => {
        const { title, body } = payload.notification;

        fetchNotificationCount();
        setStatusData();

        // nampilke notifikasi
        return toast({
          title: title,
          description: body,
          status: "info",
          duration: 5000,
          isClosable: false,
          position: "top-right",
          variant: "left-accent",
        });
      })
      .catch((err) => console.error("Error receiving message:", err));
  }, [
    currentActiveUser?.fcm_token,
    currentActiveUser?.id,
    currentActiveUser?.role,
    loggedUsers,
    toast,
    setStatusData,
    statusData,
    saveCurrentActiveUser,
    saveLoggedUsers,
    alreadyRequest,
    fetchNotificationCount,
  ]);

  useEffect(() => {
    const saveFcmTokenLoggedUsers = async () => {
      const vapidKey = `${process.env.REACT_APP_VAPID_KEY}`;

      const fcmToken = await requestFCMToken(vapidKey);
      if (fcmToken) {
        const updatedLoggedUsers = (
          loggedUsers as CurrentActiveUserInterface[]
        ).map((user) => {
          if (user.fcm_token !== fcmToken) {
            saveFcmTokenToBackend(user.id, `${user.role}`, fcmToken);
            return { ...user, fcm_token: fcmToken };
          }

          return user;
        });

        saveLoggedUsers(updatedLoggedUsers);
      }
    };

    if ("Notification" in window) {
      if (Notification.permission === "granted") {
        if (!alreadyRequest) {
          saveFcmTokenLoggedUsers();
        }
      }
    }
  }, [alreadyRequest, loggedUsers, saveLoggedUsers]);

  useEffect(() => {
    PullToRefresh.init({
      mainElement: "body", // isoh diubah sesuai elemen utama sing dikarepke
      onRefresh: () => {
        window.location.reload(); // reload
      },
    });

    return () => {
      PullToRefresh.destroyAll();
    };
  }, []);

  // ctrl + d buat shortcut ganti color mode (for development purpose only)

  return (
    <ChakraProvider theme={customTheme}>
      <ThemeToggler />
      <TawkToChat />
      <RouterProvider router={router} />
    </ChakraProvider>
  );
};
